/**
 * Canonical locale codes for the app and marketing site. The site serves one
 * path per locale (/en, /en-us, /ja, /ko, /zh-cn, /zh-tw); the web app uses
 * the same set in its language selector. zh-HK (and other zh regions) fall
 * back to zh-TW — the shared Traditional variant — via i18next language
 * detection.
 */

/**
 * URL path segments for the marketing site. Lowercase to match the /en and
 * /ja prefixes; canonical codes stay en-US/ko/zh-CN/zh-TW everywhere else
 * (i18next keys, <html lang>, Settings values).
 * @type {readonly string[]}
 */
export const SUPPORTED_URL_LOCALES = Object.freeze(['en', 'en-us', 'ja', 'ko', 'zh-cn', 'zh-tw']);

/**
 * Convert a canonical locale code to its URL path segment (zh-CN → zh-cn).
 * @param {string} locale
 * @returns {string}
 */
export function localeToUrl(locale) {
	return locale.toLowerCase();
}

/**
 * Map a browser/i18next language to a supported locale code. Region-tagged
 * codes normalise to their base locale (ja-JP → ja, ko-KR → ko). US English
 * (en-US) is preserved so US browsers auto-select the US variant; every other
 * English code — en, en-GB, en-AU, en-CA, … — falls back to British English
 * (en). Traditional is chosen only for explicitly Traditional tags (zh-tw,
 * zh-hk, zh-mo, zh-hant); everything else Chinese — bare zh, zh-cn, zh-sg,
 * zh-hans — is Simplified, matching the marketing-site edge rules. Anything
 * else falls
 * back to English. Also converts lowercase URL segments (zh-cn → zh-CN).
 * @param {string} lang
 * @returns {string}
 */
export function normalizeLocale(lang) {
	const base = lang.toLowerCase();
	if (base === 'en-us') { return 'en-US'; }
	if (base.startsWith('en')) { return 'en'; }
	if (base.startsWith('ja')) { return 'ja'; }
	if (base.startsWith('ko')) { return 'ko'; }
	if (base.startsWith('zh')) { return base.startsWith('zh-hant') || base === 'zh-tw' || base === 'zh-hk' || base === 'zh-mo' ? 'zh-TW' : 'zh-CN'; }
	return 'en';
}

/**
 * Map a site URL path segment (or canonical locale code) to its canonical
 * locale, preserving the US English variant so /en-us stays en-US. Anything
 * unknown falls back to en.
 * @param {string} segment
 * @returns {string}
 */
export function localeFromSegment(segment) {
	return normalizeLocale(segment);
}
